import { useEffect, useState } from "react";
import { Smartphone } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const b64 = (s) => { const p = "=".repeat((4 - (s.length % 4)) % 4); const raw = atob((s + p).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from([...raw].map((c) => c.charCodeAt(0))); };
const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

async function currentSub() { const reg = await navigator.serviceWorker.ready; return reg.pushManager.getSubscription(); }

export const PushSettings = ({ open, setOpen }) => {
  const { profile } = useProfile();
  const [prefs, setPrefs] = useState(null);
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    http.get("/push/prefs").then((r) => setPrefs(r.data));
    if (supported()) currentSub().then((s) => setOn(!!s)).catch(() => {});
  }, [open]);
  const enable = async () => {
    setBusy(true);
    try {
      if ((await Notification.requestPermission()) !== "granted") throw new Error("Notifications are blocked. Allow them in your browser settings.");
      const { key } = (await http.get("/push/key")).data;
      const reg = await navigator.serviceWorker.ready;
      const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64(key) }));
      await http.post("/push/subscribe", sub.toJSON());
      await http.post("/push/test");
      setOn(true); toast.success("Phone alerts are on");
    } catch (e) { toast.error(e.message?.includes("blocked") ? e.message : formatApiError(e)); }
    setBusy(false);
  };
  const disable = async () => {
    setBusy(true);
    try { const s = await currentSub(); if (s) { await http.post("/push/unsubscribe", { endpoint: s.endpoint }); await s.unsubscribe(); } setOn(false); toast.success("Phone alerts are off on this device"); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const setPref = async (k, v) => { const n = { checkin: prefs.checkin, morning: prefs.morning, live: prefs.live, weekly: prefs.weekly, missing: prefs.missing, classes: prefs.classes, chat: prefs.chat, [k]: v }; setPrefs({ ...prefs, ...n }); await http.put("/push/prefs", n).catch(() => {}); };
  const rows = [["checkin", "Check-in reminders"], ...(profile?.role === "student" ? [["morning", "Today's assignments each morning"]] : []), ["live", "Live class reminders"], ["chat", "New messages"], ...(profile?.role === "parent" ? [["weekly", "Sunday weekly summary"], ["missing", "7 AM missing work alerts"]] : [])];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#FDFBF7] max-w-md" data-testid="push-settings-dialog">
        <DialogTitle className="font-display text-2xl text-[#2C4A3E] inline-flex items-center gap-2"><Smartphone className="w-5 h-5" /> Phone alerts</DialogTitle>
        {!supported() ? (
          <p className="text-sm text-stone-600" data-testid="push-unsupported">{isIOS() && !standalone() ? "On iPhone and iPad, first install the app: tap Share, then 'Add to Home Screen'. Open it from your home screen and turn alerts on here." : "This browser doesn't support phone alerts."}</p>
        ) : (
          <div className="flex items-center justify-between rounded-2xl bg-white border border-[#E6DEC3] px-4 py-3">
            <span className="text-sm text-[#2C4A3E]">Alerts on this device</span>
            <Switch checked={on} disabled={busy} onCheckedChange={(v) => (v ? enable() : disable())} data-testid="push-device-switch" />
          </div>
        )}
        {prefs && <div className="space-y-2 mt-2">{rows.map(([k, l]) => (
          <label key={k} className="flex items-center justify-between text-sm px-1"><span>{l}</span><Switch checked={prefs[k]} onCheckedChange={(v) => setPref(k, v)} data-testid={`push-pref-${k}`} /></label>
        ))}</div>}
      </DialogContent>
    </Dialog>
  );
};
